import { Injectable } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";

import { PdfPreviewModalComponent } from "../parts/pdf/pdf-preview-modal/pdf-preview-modal.component";
import { pdfThumbnailListModalModalComponent } from "../parts/pdf/pdf-thumbnail-list-modal/pdf-thumbnail-list-modal.component";

import * as pdfjsLib from "pdfjs-dist";
// Angular5 / Webpack3 では worker 使用不可 → null にする
(pdfjsLib as any).GlobalWorkerOptions.workerSrc = null;

declare var jsPDF: any;
declare var PDFLib: any;

type Orientation = "landscape" | "portrait" | "square";

@Injectable()
export class PdfService {
  constructor(
    private sanitizer: DomSanitizer,
    private modalService: NgbModal
  ) {}

  /**
   * Angular5 / FileReader 対応版
   * @param {File} file 変換対象のPDFファイル
   * @returns {ArrayBuffer}
   */
  readAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result as ArrayBuffer);
      r.readAsArrayBuffer(file);
    });
  }

  /**
   * jsPDFで生成したPDFを作成し、ArrayBufferを返却する
   * @param {string} imageBase64  PDF化する画像のバイナリデータ
   * @param {string} extension    画像ファイルの拡張子（"PNG" or "JPEG"）
   * @returns {ArrayBuffer}
   */
  generatePdfFromImage(
    imageBase64: string,
    imageObj: any | null = null,
    extension: "JPEG" | "PNG" = "JPEG",
    marginMm: number = 5
  ): ArrayBuffer {
    let imgW = null;
    let imgH = null;

    if (imageObj == null) {
      const tmpPdf = new jsPDF();
      const props = tmpPdf.getImageProperties(imageBase64);
      imgW = props.width;
      imgH = props.height;
    } else {
      imgW = imageObj.width;
      imgH = imageObj.height;
    }

    const isLandscapeImage = imgW >= imgH;

    const a4Portrait: [number, number] = [210, 297];
    const a4Landscape: [number, number] = [297, 210];

    const pdf = new jsPDF({
      unit: "mm",
      orientation: isLandscapeImage ? "landscape" : "portrait",
      format: isLandscapeImage ? a4Landscape : a4Portrait,
    });

    const margin = Math.max(0, marginMm); // 念のため 0 未満を防ぐ

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const maxWidth = pageWidth - margin * 2;
    const maxHeight = pageHeight - margin * 2;

    const scale = Math.min(maxWidth / imgW, maxHeight / imgH);
    const drawW = imgW * scale;
    const drawH = imgH * scale;

    const x = margin + (maxWidth - drawW) / 2;
    const y = margin + (maxHeight - drawH) / 2;

    pdf.addImage(imageBase64, extension, x, y, drawW, drawH);
    return pdf.output("arraybuffer");
  }

  /**
   * PDFのArrayBufferを結合し、1つのPDFファイルとなるバイナリに変換する
   * @param {ArrayBuffer[]} pdfBuffers PDFのArrayBuffer配列
   * @returns {ArrayBuffer}
   */
  async mergePdfs(pdfBuffers: ArrayBuffer[]): Promise<Uint8Array> {
    const { PDFDocument } = PDFLib;

    const merged = await PDFDocument.create();

    for (const buffer of pdfBuffers) {
      const pdf = await PDFDocument.load(buffer); // ← atob 不要
      const pages = await merged.copyPages(pdf, pdf.getPageIndices());
      pages.forEach((p) => merged.addPage(p));
    }

    return await merged.save();
  }

  /**
   * 1つのPDFからサムネイルを生成する
   * @param {ArrayBuffer} buffer PDFのArrayBuffer
   * @returns {ArrayBuffer}
   */
  // async generateThumbnailFromPdfBuffer(buffer: ArrayBuffer): Promise<string> {
  //   const loadingTask = (pdfjsLib as any).getDocument({ data: buffer });
  //   const pdf = await loadingTask.promise;

  //   const page = await pdf.getPage(1);

  //   // ★ scale を大きめに
  //   const viewport = page.getViewport({ scale: 1.0 });

  //   const canvas = document.createElement("canvas");
  //   const ctx = canvas.getContext("2d");

  //   canvas.width = viewport.width;
  //   canvas.height = viewport.height;

  //   await page.render({
  //     canvasContext: ctx,
  //     viewport: viewport,
  //   }).promise;

  //   return canvas.toDataURL("image/png");
  // }

  // base64ToUint8Array(base64: string): Uint8Array {
  //   const binary = atob(base64.replace(/^data:application\/pdf;base64,/, ""));
  //   const len = binary.length;
  //   const bytes = new Uint8Array(len);
  //   for (let i = 0; i < len; i++) {
  //     bytes[i] = binary.charCodeAt(i);
  //   }
  //   return bytes;
  // }

  // ★ もし必要になった時の変換
  // private toArrayBuffer(binaryString: any): ArrayBuffer {
  //   const len = binaryString.length;
  //   const buffer = new ArrayBuffer(len);
  //   const view = new Uint8Array(buffer);
  //   for (let i = 0; i < len; i++) {
  //     view[i] = binaryString.charCodeAt(i);
  //   }
  //   return buffer;
  // }

  // downloadMergedPdf(pdfBytes: Uint8Array) {
  //   const safeBytes = new Uint8Array(pdfBytes.length);
  //   safeBytes.set(pdfBytes);

  //   const blob = new Blob([safeBytes], { type: "application/pdf" });

  //   const url = URL.createObjectURL(blob);
  //   const a = document.createElement("a");
  //   a.href = url;
  //   a.download = "merged.pdf";
  //   document.body.appendChild(a);
  //   a.click();
  //   a.remove();

  //   URL.revokeObjectURL(url);
  // }

  // fileToBase64(file: File): Promise<string> {
  //   return new Promise((resolve, reject) => {
  //     const reader = new FileReader();

  //     reader.onload = () => {
  //       // `data:application/pdf;base64,...` の `,` より後ろだけ取得
  //       const base64 = (reader.result as string).split(",")[1];
  //       resolve(base64);
  //     };

  //     reader.onerror = (error) => reject(error);
  //     reader.readAsDataURL(file);
  //   });
  // }

  getOrientationFromDataUrl(dataUrl: string): Promise<{
    width: number;
    height: number;
    orientation: Orientation;
  }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const width = img.naturalWidth;
        const height = img.naturalHeight;
        const orientation: Orientation =
          width > height ? "landscape" : width < height ? "portrait" : "square";
        resolve({ width, height, orientation });
      };
      img.onerror = () => reject(new Error("Failed to load base64 image"));
      img.src = dataUrl;
    });
  }

  async generateThumbnail(buffer: ArrayBuffer): Promise<string> {
    console.log("buffer length:", buffer.byteLength);

    const pdf = await (pdfjsLib as any).getDocument({ data: buffer }).promise;
    const page = await pdf.getPage(1);

    // ★ 旧API：scale を直接渡す
    const viewport = page.getViewport(1.0);
    console.log("viewport:", viewport);

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = viewport.width;
    canvas.height = viewport.height;
    console.log("canvas size:", canvas.width, canvas.height);

    await page.render({
      canvasContext: ctx,
      viewport,
    }).promise;

    return canvas.toDataURL("image/png");
  }

  /**
   * PDFプレビューモーダル用のファイルを生成
   * @param {pdfFiles} any pdfファイル群
   * @returns {ArrayBuffer}
   */
  async getPreviewPdfUrl(pdfFiles: any = []): Promise<string> {
    let previewUrl = null;
    const buffers: ArrayBuffer[] = [];
    for (const file of pdfFiles) {
      const buffer = await this.readAsArrayBuffer(file);
      buffers.push(buffer);
    }
    const merged = await this.mergePdfs(buffers);
    const safeBytes = new Uint8Array(merged.length);
    safeBytes.set(merged);
    const blob = new Blob([safeBytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    // PDF.js viewer に渡す
    const viewerUrl =
      "assets/pdfjs/web/viewer.html?file=" + encodeURIComponent(url);
    previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(viewerUrl);
    return previewUrl;
  }

  /**
   * PDFプレビュー用のモーダルを開くボタン
   * @param {file} SafeResourceUrl 変換対象のPDFファイル
   * @param {boolean} postProcess  モーダルからのアクションで後処理があるかどうか
   */
  pdfPreviewModalOpen(
    file: SafeResourceUrl,
    postProcess: boolean,
    callback: (modalresult: any) => void
  ) {
    const modalRef = this.modalService.open(PdfPreviewModalComponent, {
      windowClass: "fullscreen-modal",
      backdrop: "static",
      keyboard: false,
    });
    modalRef.componentInstance.previewUrl = file;
    modalRef.componentInstance.postProcess = postProcess;
    modalRef.result.then(
      (result) => {
        callback(result);
      },
      function () {}
    );
  }

  /**
   * PDFのサムネイル表示を行うモーダルを開く
   * @param {file} pdfFiles 表示するPDFファイル群
   */
  async pdfThumbnailListModalOpen(
    pdfFiles: File[],
    callback: (modalresult: any) => void
  ) {
    const modalRef = this.modalService.open(
      pdfThumbnailListModalModalComponent,
      {
        windowClass: "fullscreen-modal",
        backdrop: "static",
        keyboard: false,
      }
    );
    // サムネイルの生成
    let pdf = [];
    let thumbnails = [];
    const files = pdfFiles;
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      if (f.type === "application/pdf") {
        pdf.push(f);
        const buffer = await this.readAsArrayBuffer(f);
        const thumb = await this.generateThumbnail(buffer);
        thumbnails.push(thumb);
      }
    }
    modalRef.componentInstance.pdfFiles = pdf;
    modalRef.componentInstance.thumbnails = thumbnails;
    modalRef.result.then(
      (result) => {
        callback(result);
      },
      function () {}
    );
  }

  public base64ToFile(
    base64: string,
    filename: string,
    mime = "application/octet-stream"
  ): File {
    // 万一 data URI で来ても動くように対応
    const cleaned = base64.includes(",") ? base64.split(",")[1] : base64;
    const binary = atob(cleaned);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new File([bytes], filename, {
      type: mime,
      lastModified: Date.now(),
    });
  }

  async fileToBase64(file: File): Promise<string> {
    const buf = await this.readAsArrayBuffer(file);
    return this.arrayBufferToBase64(buf);
  }

  private arrayBufferToBase64(buf: ArrayBuffer): string {
    const bytes = new Uint8Array(buf);

    // typed arrayを「反復」しない（インデックスアクセスのみ）
    const chunkSize = 0x8000; // 32KB
    let binary = "";

    for (let i = 0; i < bytes.length; i += chunkSize) {
      const end = Math.min(i + chunkSize, bytes.length);
      const chars: number[] = new Array(end - i);
      for (let j = i, k = 0; j < end; j++, k++) {
        chars[k] = bytes[j];
      }
      binary += String.fromCharCode.apply(null, chars);
    }

    return btoa(binary);
  }

  /**
   * 画像ファイルをDataUrl形式に変換する関数（非同期関数）
   * @param {File} file 変換対象のファイル
   * @returns {string}  変換したDataUrl文字列
   */
  public fileToDataURL(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file); // "data:image/png;base64,...."
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/service/pdf.service.ts